import type {Messages} from './keys';

/**
 * Simplified Chinese. Falls back to Noto Sans SC (foundations §8).
 *
 * A tourist venue may see more CN sessions than Thai, so this is a first-class
 * dictionary, not a courtesy translation: same voice, same rule that an error
 * says what to do next.
 *
 * **购物车 is deliberate, and it does not track the Thai word.** Thai calls the
 * cart `รายการอาหาร` (owner's decision — see the lexicon note in `th.ts`); 购物车
 * is the word a Chinese speaker expects for the same thing on the same screen,
 * so it stays. Inheriting from Thai means inheriting the *meaning*, not the noun.
 */
export const zh: Messages = {
  'common.appName': 'pokpokPOS',
  'common.attribution': '由 pokpokPOS 提供点餐',
  'common.back': '返回',
  'common.close': '关闭',
  'common.cancel': '取消',
  'common.confirm': '确认',
  'common.retry': '再试一次',
  'common.continue': '继续',
  'common.done': '完成',
  'common.ok': '好',
  'common.loading': '加载中…',
  'common.askStaff': '可以询问店员',
  'common.table': '{label} 号桌',
  'common.viewMenu': '查看菜单',
  'common.quantity': '数量',
  'common.item': '菜品',
  'common.items': '{count} 项',
  'common.language': '语言',
  'common.changeLanguage': '切换语言',
  'common.subtotal': '小计',
  'common.total': '合计',
  'common.discount': '折扣',
  'common.serviceCharge': '服务费 {rate}%',
  'common.rounding': '尾数调整',
  'common.tip': '小费',
  'common.vatIncluded': '已含 {rate}% 增值税',
  'common.vatAdded': '增值税 {rate}%',
  'common.free': '不额外收费',
  'common.noPhoto': '暂无图片',
  'common.offline': '离线',
  'common.online': '已连接',
  'common.reconnecting': '正在重新连接…',
  'common.stale': '信息可能不是最新',
  'common.soldOut': '已售完',
  'common.updatedAt': '更新于 {time}',
  'common.minutesAgo': '{minutes} 分钟前',
  'common.justNow': '刚刚',
  'common.skipToContent': '跳到主要内容',

  'reason.eighty_six': '厨房已售完',
  'reason.out_of_stock': '缺货',
  'reason.off_schedule': '此时段不供应',
  'reason.channel_hidden': '此菜单没有',
  'reason.deactivated': '暂停供应',

  'error.generic': '没有成功，请再试一次',
  'error.network': '网络不通，请再试一次',
  'error.session': '二维码已失效，请重新扫码',
  'error.forbidden': '这里无法操作，请询问店员',
  'error.notFound': '找不到这一项',
  'error.itemUnavailable': '有菜品已售完',
  'error.rateLimited': '操作太频繁，请稍候再试',
  'error.validation': '信息不完整，请检查后再试',
  'error.conflict': '内容已变动，请重新查看',
  'error.tableHasOpenBill': '这桌已经下过单了，可查看已点的菜品',
  'error.unavailable': '餐厅系统暂时无法响应，请询问店员',
  'error.notAvailableHere': '本店尚未开通此功能',

  'q1.welcome': '欢迎光临',
  'q1.openingSession': '正在打开菜单…',
  'q1.youAreAt': '您在 {table}',
  'q1.tableUnknown': '在此桌点餐',
  'q1.startOrdering': '开始点餐',
  'q1.chooseLanguage': '选择语言',
  'q1.orderFirstNote': '先点餐，用餐后到柜台结账',
  'q1.payFirstUnavailable': '本店需先付款，请向店员点餐',
  'q1.retryOpening': '重新打开',

  'q2.title': '语言',
  'q2.th': 'ไทย',
  'q2.en': 'English',
  'q2.zh': '中文',
  // 说的是「已经保留」，不是「会保留」——切换语言时菜品并没有离开过。
  'q2.cartKept': '您选好的菜品都还在',

  'q3.title': '菜单',
  'q3.searchLabel': '搜索菜单',
  'q3.searchPlaceholder': '搜索菜名',
  'q3.searchClear': '清除搜索',
  'q3.searchNoResults': '没有找到“{query}”',
  'q3.searchNoResultsAction': '换个词试试，或从上方分类选择',
  'q3.allCategories': '全部',
  'q3.featuredHeading': '推荐',
  'q3.emptyCategory': '此分类暂时没有菜品',
  'q3.loading': '正在加载菜单…',
  'q3.menuUnavailable': '菜单加载失败',
  'q3.menuUnavailableAction': '请再试一次，或询问店员',
  'q3.channelPriceNote': '此为扫码点餐的价格',
  'q3.itemJustSoldOut': '“{item}”刚刚售完',
  'q3.viewCart': '查看购物车',
  'q3.viewCartWithCount': '查看购物车 · {count} 项',
  'q3.markerSpicy': '辣',
  'q3.markerVerySpicy': '很辣',
  'q3.markerVegetarian': '素食',
  'q3.markerVegan': '纯素',
  'q3.markerHalal': '清真',
  'q3.markerContainsPork': '含猪肉',
  'q3.markerContainsNuts': '含坚果',
  'q3.markerContainsSeafood': '含海鲜',

  'q4.description': '介绍',
  'q4.noDescription': '暂无介绍',
  'q4.required': '必选',
  'q4.optional': '可选',
  'q4.chooseExactly': '请选 {count} 项',
  'q4.chooseAtLeast': '至少选 {count} 项',
  'q4.chooseUpTo': '最多选 {count} 项',
  'q4.chooseBetween': '请选 {min}–{max} 项',
  'q4.requiredUnmet': '还没有选好',
  'q4.maxReached': '已选满',
  'q4.modifierUnavailable': '此选项已售完',
  'q4.increase': '增加一份',
  'q4.decrease': '减少一份',
  'q4.noteToKitchen': '给厨房的备注',
  // 两个例子，不能只给一个：只给一个会被读成「只能提这一种要求」。
  'q4.notePlaceholder': '例如：不要香菜、不要豇豆',
  'q4.noteHonesty': '厨房可能看不懂其他语言，忙时也可能来不及看',
  'q4.noteUnavailable': '本店暂不接受给厨房的备注',
  'q4.runningPrice': '价格',
  'q4.addToCart': '加入购物车',
  'q4.addToCartWithPrice': '加入购物车 · {price}',
  'q4.added': '已加入购物车',
  'q4.soldOutWhileOpen': '抱歉，“{item}”刚刚售完',
  'q4.soldOutAction': '请另选一款',
  'q4.backToMenu': '回到菜单',

  'q5.title': '购物车',
  'q5.empty': '购物车还是空的',
  'q5.emptyAction': '去看菜单',
  'q5.remove': '删除',
  'q5.removed': '已删除“{item}”',
  'q5.undo': '撤销',
  'q5.edit': '修改',
  'q5.addMore': '再点一些',
  'q5.yourItems': '您点的',
  'q5.tableItems': '同桌其他人点的',
  'q5.notPaidYet': '尚未付款',
  'q5.estimatedTotal': '预估金额',
  'q5.estimateNote': '实际金额由餐厅在下单时计算',
  'q5.confirmTitle': '确认下单？',
  'q5.confirmBody': '立即送到厨房 — {count} 项，约 {total}，用餐后到柜台结账',
  'q5.confirmSend': '确认下单',
  'q5.lineSoldOut': '“{item}”已售完',
  'q5.lineSoldOutAction': '从购物车移除',
  'q5.linePriceChanged': '“{item}”现价 {price}',
  'q5.linePriceChangedAction': '知道了',
  'q5.overVenueLimit': '每次最多可点 {limit} 项',
  'q5.placeOrder': '提交订单',
  'q5.reviewOrder': '立即下单',

  'q7.title': '确认订单',
  'q7.summary': '订单内容',
  'q7.submit': '送到厨房',
  'q7.submitting': '正在提交…',
  'q7.payLaterNote': '现在送到厨房，用餐后到柜台结账',
  'q7.sentTitle': '已送到厨房',
  'q7.sentBody': '厨房已收到，菜马上就做，用餐后到柜台结账',
  'q7.viewStatus': '查看已下单的菜品',
  'q7.orderAgain': '再点一些',
  'q7.rejectedTitle': '餐厅尚未接受此订单',
  'q7.rejectedAction': '请询问店员',
  'q7.connectionLostTitle': '没有送出，网络中断',
  'q7.connectionLostAction': '再按一次即可，不会重复下单',
  'q7.queued': '正在自动重新发送',
  'q7.someItemsSoldOut': '有菜品已售完',
  'q7.backToCart': '返回修改',
  'q7.alreadySent': '此订单已经送出',

  'q8.title': '付款',
  'q8.amountDue': '应付金额',
  'q8.handoffNote': '点击后将前往 ReservePay 的付款页面，在那里选择付款方式',
  'q8.methodsNote': '付款方式在下一页选择',
  'q8.leavingNote': '付款页面属于 ReservePay，不是本店的页面',
  'q8.pay': '前往付款',
  'q8.paying': '正在打开付款页面…',
  'q8.expiresIn': '请在 {minutes} 分钟内付款',
  'q8.expiresSoon': '剩余不到 1 分钟',
  'q8.nothingToPay': '此账单已付清',
  'q8.resume': '返回付款页面',
  'q8.backToBill': '返回账单',
  'q8.payAtCounterInstead': '也可以到柜台付款',
  'q8.unavailableTitle': '本店暂不支持 QR 付款',
  'q8.noRedirectTitle': '暂时无法打开付款页面',
  'q8.reconcileTitle': '无法确认付款是否已经发起',
  'q8.reconcileBody': '请勿重复操作，先请店员核对',
  'q8.amountChanged': '账单金额已变动，请重新确认金额',

  'q9.title': '付款结果',
  'q9.checking': '正在查询…',
  'q9.verifiedTitle': '付款完成',
  // 「无需出示付款凭证」才是这张绿色卡片的重点：客人可以把手机收起来。
  // 凭证 is the word this dictionary already uses for a สลิป (`q9.unconfirmedBody`).
  'q9.verifiedBody': '餐厅已收到 ReservePay 的确认，无需出示付款凭证',
  'q9.unconfirmedTitle': '暂时无法确认付款',
  'q9.unconfirmedBody': '如果已经付款，请在柜台出示凭证，店家会核对并结清账单',
  'q9.paymentTakenTitle': 'ReservePay 已确认付款',
  'q9.paymentTakenBody': '餐厅尚未结清账单，请在柜台出示此页面',
  'q9.failedTitle': '付款未成功',
  'q9.failedBody': '请重试，或到柜台付款',
  'q9.expiredTitle': '付款时间已结束',
  'q9.expiredBody': '可以重新发起付款',
  'q9.payAgain': '重新付款',
  'q9.noAttempt': '尚未发起付款',
  'q9.recheck': '再次查询',
  'q9.startedAt': '发起付款时间 {time}',
  'q9.returnNotice': '有一笔 {amount} 的付款待确认',
  'q9.returnNoticeAction': '查看付款结果',
  'q9.refundNote': '退款仅可在柜台办理',

  'q12.title': '提前点餐',
  'q12.takeawayNote': '提前点餐，到柜台取餐',
  'q12.startOrdering': '开始点餐',
  'q12.pickupNote': '点完后在柜台等叫号',
  'q12.queueLength': '目前 {count} 位在排队',
  'q12.queueUnknown': '暂时无法得知排队人数',
  'q12.waitEstimate': '大约等 {minutes} 分钟',
  'q12.waitUnknown': '暂时无法得知等候时间',

  'q14.title': '餐点进度',
  'q14.noOrder': '还没有订单',
  'q14.noOrderAction': '去看菜单',
  'q14.statePending': '等待厨房',
  'q14.stateFired': '厨房已收到',
  'q14.stateInProgress': '制作中',
  'q14.stateReady': '已做好',
  'q14.stateServed': '已上菜',
  'q14.stateCancelled': '已取消',
  'q14.received': '餐厅已收到您的订单',
  'q14.progressLabel': '订单进度',
  'q14.elapsed': '已过 {minutes} 分钟',
  'q14.perItemHeading': '菜品',
  'q14.staleTitle': '进度可能不是最新',
  'q14.staleBody': '暂时联系不上厨房，可以询问店员',
  'q14.billLater': '用餐后到柜台结账',
  'q14.orderAgain': '再点一些',
  'q14.orderCancelled': '此订单已取消',
  'q14.orderVoided': '餐厅已取消此订单',

  // --- Q14 · the two asks ---------------------------------------------------
  'q14.callStaff': '呼叫服务员',
  'q14.callStaffSent': '已呼叫服务员',
  'q14.callStaffPending': '已呼叫',
  'q14.requestBill': '请结账',
  'q14.requestBillSent': '已通知服务员结账',
  'q14.requestBillDone': '已请求结账',
  'q14.askFailed': '发送失败，请再试一次',

  'q17.title': '订单详情',
  'q17.thanks': '谢谢惠顾',
  'q17.orderNumber': '订单 {number}',
  'q17.placedAt': '下单时间 {time}',
  'q17.itemsHeading': '菜品',
  'q17.paid': '已收到款项',
  'q17.paidAt': '付款时间 {time}',
  'q17.unpaid': '尚未付款',
  'q17.balanceDue': '待付金额',
  'q17.payAtCounter': '到柜台付款',
  'q17.payOnline': '在线付款',
  'q17.refunded': '已退款',
  'q17.partiallyRefunded': '已部分退款',
  'q17.venueHeading': '餐厅',
  'q17.keepThisPage': '此页面可以随时再打开',
  'q17.taxInvoice': '税务发票',
  'q17.taxInvoiceAtCounter': '如需税务发票，请到柜台索取',
  'q17.notFound': '找不到此订单',
  'q17.notFoundAction': '请询问店员',
  'q17.processorDisclosure': '款项由 ReservePay 处理',

  'q19.invalidTitle': '此二维码无法使用',
  'q19.expiredTitle': '此二维码已过期',
  'q19.regeneratedTitle': '此桌已换新的二维码',
  'q19.body': '请重新扫描桌上的二维码，或向店员索取新的',
  'q19.rescan': '重新扫码',
  'q19.noQrTitle': '尚未扫码',
  'q19.noQrBody': '请扫描桌上的二维码进入此页面',

  'q20.closedTitle': '餐厅已打烊',
  'q20.outsideHoursTitle': '还没到营业时间',
  'q20.notAcceptingTitle': '目前暂停扫码点餐',
  'q20.notAcceptingBody': '请向店员点餐',
  'q20.reopensAt': '{time} 再次营业',
  'q20.reopensUnknown': '未提供营业时间',
  'q20.hoursHeading': '营业时间',
  'q20.hoursToday': '今天 {hours}',
  'q20.hoursUnknown': '未提供营业时间',
  'q20.browseAnyway': '仍可先看看菜单',
  'q20.day.mon': '星期一',
  'q20.day.tue': '星期二',
  'q20.day.wed': '星期三',
  'q20.day.thu': '星期四',
  'q20.day.fri': '星期五',
  'q20.day.sat': '星期六',
  'q20.day.sun': '星期日',

  // --- booking · customer web (BookWeb / BookDone / BookManage) ------------
  'book.title': '预订餐桌',
  'book.lead': '选择日期和时间，然后填写联系方式',
  'book.dateLabel': '日期',
  'book.timeLabel': '时间',
  'book.partyLabel': '人数',
  'book.nameLabel': '预订人',
  'book.namePlaceholder': '怎么称呼您',
  'book.phoneLabel': '手机号',
  'book.phonePlaceholder': '08x-xxx-xxxx',
  'book.today': '今天',
  'book.pickDate': '选择 {date}',
  'book.pickTime': '选择 {time}',
  'book.partyDecrease': '减少人数',
  'book.partyIncrease': '增加人数',
  'book.partyValueLabel': '{count} 人',
  'book.people': '{count} 人',
  'book.summary': '{date} {time} · {count} 人',
  'book.holdNote': '餐厅会再次确认您的预订',
  'book.submit': '确认预订',
  'book.submitting': '预订中…',
  'book.needName': '请先填写预订人',
  'book.needPhone': '请先填写手机号',
  'book.needTime': '请先选择时间',
  'book.doneTitle': '预订成功',
  'book.doneBody': '餐厅已收到您的预订',
  'book.detailsHeading': '预订详情',
  'book.fieldDate': '日期',
  'book.fieldTime': '时间',
  'book.fieldParty': '人数',
  'book.fieldName': '预订人',
  'book.fieldPhone': '手机号',
  'book.fieldReference': '预订号',
  'book.fieldStatus': '状态',
  'book.saveLinkNote': '保存本页链接，以便日后查看或取消预订',
  'book.manageAction': '管理预订',
  'book.manageTitle': '预订',
  'book.manageTitleRef': '预订 {reference}',
  'book.statusPending': '待确认',
  'book.statusConfirmed': '已确认',
  'book.statusSeated': '已入座',
  'book.statusCompleted': '已完成',
  'book.statusCancelled': '已取消',
  'book.statusNoShow': '未到店',
  'book.statusUnknown': '状态未知',
  'book.cancelHeading': '取消预订',
  'book.cancelNote': '预订时间前 2 小时可免费取消，之后请致电餐厅。',
  'book.cancelAction': '取消预订',
  'book.cancelling': '取消中…',
  'book.cancelConfirmTitle': '取消此预订？',
  'book.cancelConfirmBody': '取消后无法恢复',
  'book.cancelConfirmYes': '确认取消',
  'book.keepBooking': '保留预订',
  'book.cancelledTitle': '预订已取消',
  'book.cancelledBody': '餐厅已收到您的取消',
  'book.cannotCancelWindow': '距预订时间不足 2 小时，无法在此取消，请致电餐厅。',
  'book.cannotCancelTerminal': '此预订已无法取消',
  'book.cancelWindowError': '已来不及在此取消，请致电餐厅。',
  'book.notFoundTitle': '未找到该预订',
  'book.notFoundBody': '链接可能已失效或有误，请咨询餐厅。',
  // --- Feedback · post-bill rating ----------------------------------------
  'feedback.title': '这顿饭怎么样？',
  'feedback.subtitle': '给个评分，也告诉餐厅您的想法',
  'feedback.ratingLabel': '评分',
  'feedback.starLabel': '{count} 星',
  'feedback.commentLabel': '评价（选填）',
  'feedback.commentHint': '无需填写姓名或电话',
  'feedback.commentPlaceholder': '还有什么想告诉餐厅的吗？',
  'feedback.ratingRequired': '请先选择星级',
  'feedback.submit': '提交评价',
  'feedback.submitting': '提交中…',
  'feedback.failedTitle': '评价未能提交',
  'feedback.failedAction': '可以再试一次',
  'feedback.sentTitle': '感谢您的评价',
  'feedback.sentBody': '餐厅已收到您的评价',
};
